import type { VideoProvider, VideoSourceType } from '@builder4/shared/types/video';

type ParsedVideoUrl =
  | { provider: VideoProvider; videoId: string; isValid: true }
  | { provider: null; videoId: ''; isValid: false };

// Provider URL patterns - each pattern's first capture group extracts the video ID
// unless a custom extractId function is provided
interface ProviderPatternEntry {
  regex: RegExp;
  extractId?: (match: RegExpMatchArray) => string;
}

const PROVIDER_PATTERNS: { provider: VideoProvider; patterns: ProviderPatternEntry[] }[] = [
  {
    provider: 'youtube',
    patterns: [
      {
        regex:
          /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube(?:-nocookie)?\.com\/embed\/)([a-zA-Z0-9_-]{11})/,
      },
      { regex: /youtube\.com\/shorts\/([a-zA-Z0-9_-]{11})/ },
    ],
  },
  {
    provider: 'vimeo',
    patterns: [{ regex: /vimeo\.com\/(\d+)/ }, { regex: /player\.vimeo\.com\/video\/(\d+)/ }],
  },
  {
    provider: 'loom',
    patterns: [{ regex: /loom\.com\/(?:share|embed)\/([a-f0-9]+)/ }],
  },
  {
    provider: 'wistia',
    patterns: [
      { regex: /wistia\.com\/medias\/([a-zA-Z0-9]+)/ },
      { regex: /fast\.wistia\.(?:net|com)\/embed\/iframe\/([a-zA-Z0-9]+)/ },
      { regex: /wistia\.com\/.*[?&]wmediaid=([a-zA-Z0-9]+)/ },
    ],
  },
  {
    provider: 'vidyard',
    patterns: [{ regex: /(?:share\.vidyard\.com\/watch|play\.vidyard\.com)\/([a-zA-Z0-9]+)/ }],
  },
  {
    provider: 'brightcove',
    patterns: [
      {
        regex: /players\.brightcove\.net\/(\d+)\/([^/]+)\/index\.html.*?videoId=(\d+)/,
        extractId: (match: RegExpMatchArray) => `${match[1]}:${match[2]}:${match[3]}`,
      },
    ],
  },
  {
    provider: 'cloudflare-stream',
    patterns: [
      { regex: /watch\.cloudflarestream\.com\/([a-zA-Z0-9]+)/ },
      { regex: /iframe\.videodelivery\.net\/(?:watch\/)?([a-zA-Z0-9]+)/ },
      { regex: /[a-zA-Z0-9-]+\.cloudflarestream\.com\/([a-zA-Z0-9]+)/ },
    ],
  },
];

/**
 * Parse video URL to extract provider and video ID.
 * Supports YouTube, Vimeo, Loom, Wistia, Vidyard, Brightcove, and Cloudflare Stream.
 */
export function parseVideoUrl(url: string): ParsedVideoUrl {
  if (!url) {
    return { provider: null, videoId: '', isValid: false };
  }

  for (const { provider, patterns } of PROVIDER_PATTERNS) {
    for (const { regex, extractId } of patterns) {
      const match = url.match(regex);
      if (match) {
        const videoId = extractId ? extractId(match) : match[1];
        return { provider, videoId, isValid: true };
      }
    }
  }

  return { provider: null, videoId: '', isValid: false };
}

interface EmbedUrlOptions {
  autoplay?: boolean;
  controls?: boolean;
  loop?: boolean;
  mute?: boolean;
}

// Provider embed configuration
interface EmbedConfig {
  baseUrl: (videoId: string) => string;
  autoplayOnParams: Record<string, string>;
  autoplayOffParams: Record<string, string>;
  noControlsParams: Record<string, string>;
  muteOnParams: Record<string, string>;
  loopOnParams: (videoId: string) => Record<string, string>;
  defaultParams?: Record<string, string>;
}

const EMBED_CONFIG: Record<VideoProvider, EmbedConfig> = {
  youtube: {
    baseUrl: (id) => `https://www.youtube.com/embed/${id}`,
    autoplayOnParams: { autoplay: '1' },
    autoplayOffParams: { autoplay: '0' },
    noControlsParams: { controls: '0' },
    muteOnParams: { mute: '1' },
    loopOnParams: (videoId) => ({ loop: '1', playlist: videoId }),
    defaultParams: { rel: '0' },
  },
  vimeo: {
    baseUrl: (id) => `https://player.vimeo.com/video/${id}`,
    autoplayOnParams: { autoplay: '1' },
    autoplayOffParams: { autoplay: '0' },
    noControlsParams: { controls: '0' },
    muteOnParams: { muted: '1' },
    loopOnParams: () => ({ loop: '1' }),
  },
  loom: {
    baseUrl: (id) => `https://www.loom.com/embed/${id}`,
    autoplayOnParams: { autoplay: '1' },
    autoplayOffParams: { autoplay: '0' },
    noControlsParams: { hide_owner: 'true', hide_share: 'true', hide_title: 'true' },
    muteOnParams: { muted: '1' },
    loopOnParams: () => ({}),
  },
  wistia: {
    baseUrl: (id) => `https://fast.wistia.net/embed/iframe/${id}`,
    autoplayOnParams: { autoPlay: 'true' },
    autoplayOffParams: { autoPlay: 'false' },
    noControlsParams: {
      controlsVisibleOnLoad: 'false',
      playbar: 'false',
      playButton: 'false',
      smallPlayButton: 'false',
      volumeControl: 'false',
      fullscreenButton: 'false',
      settingsControl: 'false',
    },
    muteOnParams: { muted: 'true' },
    loopOnParams: () => ({ endVideoBehavior: 'loop' }),
  },
  vidyard: {
    baseUrl: (id) => `https://play.vidyard.com/${id}`,
    autoplayOnParams: { autoplay: '1' },
    autoplayOffParams: { autoplay: '0' },
    noControlsParams: {},
    muteOnParams: { muted: '1' },
    loopOnParams: () => ({}),
  },
  brightcove: {
    baseUrl: (id) => {
      const [accountId, playerId, videoId] = id.split(':');
      return `https://players.brightcove.net/${accountId}/${playerId}/index.html?videoId=${videoId}`;
    },
    autoplayOnParams: { autoplay: 'true' },
    autoplayOffParams: {},
    noControlsParams: {},
    muteOnParams: { muted: 'true' },
    loopOnParams: () => ({}),
  },
  'cloudflare-stream': {
    baseUrl: (id) => `https://iframe.videodelivery.net/${id}`,
    autoplayOnParams: { autoplay: 'true' },
    autoplayOffParams: {},
    noControlsParams: { controls: 'false' },
    muteOnParams: { muted: 'true' },
    loopOnParams: () => ({ loop: 'true' }),
  },
};

/**
 * Generate embed URL for a video provider.
 */
export function generateEmbedUrl(
  provider: VideoProvider,
  videoId: string,
  options: EmbedUrlOptions = {},
): string {
  if (!videoId) {
    return '';
  }

  const config = EMBED_CONFIG[provider];
  const { autoplay = false, controls = true, loop = false, mute = false } = options;
  const url = new URL(config.baseUrl(videoId));

  if (config.defaultParams) {
    for (const [k, v] of Object.entries(config.defaultParams)) {
      url.searchParams.set(k, v);
    }
  }

  // Always set autoplay param to override provider defaults
  const autoplayParams = autoplay ? config.autoplayOnParams : config.autoplayOffParams;
  for (const [k, v] of Object.entries(autoplayParams)) {
    url.searchParams.set(k, v);
  }

  // WHY: Browsers require mute for autoplay to work, so mute when either explicitly set or autoplay is on
  if (mute || autoplay) {
    for (const [k, v] of Object.entries(config.muteOnParams)) {
      url.searchParams.set(k, v);
    }
  }

  if (loop) {
    const loopParams = config.loopOnParams(videoId);
    for (const [k, v] of Object.entries(loopParams)) {
      url.searchParams.set(k, v);
    }
  }

  if (!controls) {
    for (const [k, v] of Object.entries(config.noControlsParams)) {
      url.searchParams.set(k, v);
    }
  }

  return url.toString();
}

const oembedThumbnailCache = new Map<string, string | null>();

export async function getVideoThumbnailUrl(
  provider: VideoProvider,
  videoId: string,
): Promise<string | null> {
  if (!videoId) {
    return null;
  }

  switch (provider) {
    case 'youtube':
      return `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
    case 'wistia':
      return `https://fast.wistia.com/embed/medias/${videoId}/swatch`;
    case 'vidyard':
      return `https://play.vidyard.com/${videoId}.jpg`;
    case 'cloudflare-stream':
      return `https://videodelivery.net/${videoId}/thumbnails/thumbnail.jpg`;
    case 'vimeo':
      return fetchOembedThumbnail(
        `https://vimeo.com/api/oembed.json?url=https://vimeo.com/${videoId}`,
        videoId,
      );
    case 'loom':
      return fetchOembedThumbnail(
        `https://www.loom.com/v1/oembed?url=https://www.loom.com/share/${videoId}`,
        videoId,
        loomStaticThumbnail,
      );
    case 'brightcove':
      return fetchBrightcoveThumbnail(videoId);
    default:
      return null;
  }
}

// WHY: Loom oEmbed returns animated GIFs (e.g. ...-with-play.gif). Convert to static first-frame JPG.
function loomStaticThumbnail(url: string): string {
  return url.replace(/-with-play\.gif$/, '-00001.jpg').replace(/\.gif$/, '.jpg');
}

async function fetchOembedThumbnail(
  oembedUrl: string,
  cacheKey: string,
  transformUrl?: (url: string) => string,
): Promise<string | null> {
  if (oembedThumbnailCache.has(cacheKey)) {
    return oembedThumbnailCache.get(cacheKey) ?? null;
  }

  try {
    const response = await fetch(oembedUrl);
    if (!response.ok) {
      oembedThumbnailCache.set(cacheKey, null);
      return null;
    }
    const data = await response.json();
    let thumbnailUrl = (data.thumbnail_url as string) || null;
    if (thumbnailUrl && transformUrl) {
      thumbnailUrl = transformUrl(thumbnailUrl);
    }
    oembedThumbnailCache.set(cacheKey, thumbnailUrl);
    return thumbnailUrl;
  } catch {
    oembedThumbnailCache.set(cacheKey, null);
    return null;
  }
}

async function fetchBrightcoveThumbnail(compositeId: string): Promise<string | null> {
  if (oembedThumbnailCache.has(compositeId)) {
    return oembedThumbnailCache.get(compositeId) ?? null;
  }

  const [accountId, playerId, videoId] = compositeId.split(':');
  if (!accountId || !playerId || !videoId) {
    return null;
  }

  try {
    const configUrl = `https://players.brightcove.net/${accountId}/${playerId}/config.json`;
    const configResponse = await fetch(configUrl);
    if (!configResponse.ok) {
      oembedThumbnailCache.set(compositeId, null);
      return null;
    }
    const config = await configResponse.json();
    const policyKey = config.video_cloud?.policy_key || config.policy_key;
    if (!policyKey) {
      oembedThumbnailCache.set(compositeId, null);
      return null;
    }

    const apiUrl = `https://edge.api.brightcove.com/playback/v1/accounts/${accountId}/videos/${videoId}`;
    const videoResponse = await fetch(apiUrl, {
      headers: { Accept: `application/json;pk=${policyKey}` },
    });
    if (!videoResponse.ok) {
      oembedThumbnailCache.set(compositeId, null);
      return null;
    }
    const videoData = await videoResponse.json();
    const thumbnailUrl = (videoData.poster as string) || (videoData.thumbnail as string) || null;
    oembedThumbnailCache.set(compositeId, thumbnailUrl);
    return thumbnailUrl;
  } catch {
    oembedThumbnailCache.set(compositeId, null);
    return null;
  }
}

/**
 * Extract iframe src from embed code.
 */
export function extractIframeSrc(embedCode: string): string | null {
  if (!embedCode) {
    return null;
  }
  const match = embedCode.match(/src=["']([^"']+)["']/);
  return match ? match[1] : null;
}

interface EmbedValidationResult {
  isValid: boolean;
  error?: string;
}

const ALLOWED_EMBED_DOMAINS = [
  'www.youtube.com',
  'youtube.com',
  'www.youtube-nocookie.com',
  'youtube-nocookie.com',
  'player.vimeo.com',
  'www.loom.com',
  'loom.com',
  'fast.wistia.net',
  'fast.wistia.com',
  'play.vidyard.com',
  'players.brightcove.net',
  'iframe.videodelivery.net',
];

function isAllowedEmbedDomain(hostname: string): boolean {
  if (ALLOWED_EMBED_DOMAINS.includes(hostname)) {
    return true;
  }
  // Cloudflare Stream customer subdomains
  if (hostname.endsWith('.cloudflarestream.com')) {
    return true;
  }
  return false;
}

export interface BackgroundEmbedUrlOptions {
  autoplay?: boolean;
}

/**
 * Generate embed URL for background video with optional autoplay, loop, muted, no controls.
 * Suitable for background videos that should play silently and continuously.
 */
export function generateBackgroundEmbedUrl(
  url: string,
  options: BackgroundEmbedUrlOptions = {},
): string | null {
  const parsed = parseVideoUrl(url);
  if (!parsed.isValid) {
    return null;
  }

  const { autoplay = true } = options;

  // Get base embed URL with optional autoplay and no controls
  const baseUrl = generateEmbedUrl(parsed.provider, parsed.videoId, {
    autoplay,
    controls: false,
  });
  const bgUrl = new URL(baseUrl);

  // Add provider-specific loop and background parameters
  switch (parsed.provider) {
    case 'youtube':
      bgUrl.searchParams.set('loop', '1');
      bgUrl.searchParams.set('playlist', parsed.videoId); // Required for YouTube loop
      bgUrl.searchParams.set('playsinline', '1');
      break;
    case 'vimeo':
      bgUrl.searchParams.set('loop', '1');
      bgUrl.searchParams.set('background', '1'); // Vimeo background mode
      break;
    case 'wistia':
      bgUrl.searchParams.set('endVideoBehavior', 'loop');
      bgUrl.searchParams.set('playButton', 'false');
      break;
    case 'cloudflare-stream':
      bgUrl.searchParams.set('loop', 'true');
      break;
    // loom, vidyard, brightcove: no standard loop params via URL
    default:
      break;
  }

  return bgUrl.toString();
}

/**
 * Validate embed code for security.
 * - Must be a valid iframe
 * - src must be HTTPS from an allowed video provider domain
 */
export function validateEmbedCode(embedCode: string): EmbedValidationResult {
  if (!embedCode || !embedCode.trim()) {
    return { isValid: true }; // Empty is valid (just means no video set)
  }

  // Check if it contains an iframe
  if (!/<iframe\s/i.test(embedCode)) {
    return { isValid: false, error: 'Embed code must contain an iframe element' };
  }

  // Extract src
  const src = extractIframeSrc(embedCode);
  if (!src) {
    return { isValid: false, error: 'Could not find src attribute in iframe' };
  }

  // Validate URL is valid HTTPS and from an allowed domain
  try {
    const url = new URL(src);
    if (url.protocol !== 'https:') {
      return { isValid: false, error: 'Iframe src must be a valid HTTPS URL' };
    }
    if (!isAllowedEmbedDomain(url.hostname)) {
      return {
        isValid: false,
        error:
          'Iframe src must be from a supported video provider (YouTube, Vimeo, Loom, Wistia, Vidyard, Brightcove, or Cloudflare Stream)',
      };
    }
  } catch {
    return { isValid: false, error: 'Iframe src must be a valid HTTPS URL' };
  }

  return { isValid: true };
}

export interface GetBgVideoEmbedSrcOptions {
  autoplay?: boolean;
}

/**
 * Get background video embed source URL based on source type.
 * Handles both URL mode (via generateBackgroundEmbedUrl) and embed code mode (via extractIframeSrc).
 */
export function getBgVideoEmbedSrc(
  sourceType: VideoSourceType | undefined,
  videoUrl: string | null | undefined,
  embedCode: string | null | undefined,
  options: GetBgVideoEmbedSrcOptions = {},
): string | null {
  const { autoplay = true } = options;
  const embedOptions = { autoplay };

  if (sourceType === 'url' && videoUrl) {
    return generateBackgroundEmbedUrl(videoUrl, embedOptions);
  }
  if (sourceType === 'embed' && embedCode) {
    const src = extractIframeSrc(embedCode);
    return src ? generateBackgroundEmbedUrl(src, embedOptions) : null;
  }
  return null;
}
